import React from 'react';
import { Button } from 'react-bootstrap';
import BayLogo from '../BayLogo';
import './ErrorElement.scss';

/**
 * ErrorElement - A flexible error display component
 *
 * Features:
 * - Displays error pages with optional navigation bar
 * - Shows custom or default error messages
 * - Displays error details in development mode when error object is provided
 * - Supports custom click handlers for refresh and return buttons
 * - Integrates with React Error Boundaries via resetErrorBoundary prop
 * - Fully customizable appearance and behavior
 */
interface ErrorElementProps {
	showNav?: boolean;
	platform?: 'social' | 'financial' | 'client' | 'design';
	topPadding?: string;
	title?: string;
	bodyText?: string;
	imgSrc?: string;
	error?: Error | unknown;
	showRefresh?: boolean;
	refreshButtonText?: string;
	onRefreshClick?: () => void;
	showReturn?: boolean;
	returnButtonText?: string;
	returnLink?: string;
	onReturnClick?: () => void;
	resetErrorBoundary?: () => void;
	children?: React.ReactNode;
}

const ErrorElement: React.FC<ErrorElementProps> = ({
	showNav = true,
	platform = 'social',
	topPadding = '70px',
	title = 'Oops, Something Went Wrong!',
	bodyText,
	imgSrc,
	error,
	showRefresh = true,
	refreshButtonText = 'Refresh Page',
	onRefreshClick,
	showReturn = true,
	returnButtonText = 'Return to Log In page',
	returnLink = '/login',
	onReturnClick,
	resetErrorBoundary,
	children,
}) => {
	// Determine the refresh click handler
	const handleRefreshClick = () => {
		if (onRefreshClick) {
			onRefreshClick();
		} else if (resetErrorBoundary) {
			resetErrorBoundary();
		} else {
			window.location.reload();
		}
	};

	// Determine the return click handler
	const handleReturnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (onReturnClick) {
			e.preventDefault();
			onReturnClick();
		}
		// Otherwise, let the href handle navigation
	};
	if (children) {
		return <>{children}</>;
	}

	return (
		<>
			{showNav && (
				<nav
					className="navbar bg-light fixed border-bottom"
					style={{ height: 65 }}
				>
					<div className="container">
						<span className="navbar-brand">
							<BayLogo platform={platform} size="30px" logoStyle="full" />
						</span>
					</div>
				</nav>
			)}
			<main>
				<div className="container">
					<div
						className="row d-flex align-items-center justify-content-center w-100"
						style={{ paddingTop: topPadding }}
					>
						<div className="col-md-6 col-12">
							<h1 className="maintenance-page-header mb-3">
								{title}
							</h1>

							{bodyText ? (
								<p>{bodyText}</p>
							) : error ? (
								<>
									<p>
										An unexpected error occurred while processing your request.
									</p>
									{process.env.NODE_ENV === 'development' && (
										<details style={{ marginTop: '1rem' }}>
											<summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
												Error Details (Development Only)
											</summary>
											<pre style={{
												marginTop: '0.5rem',
												padding: '1rem',
												background: '#f5f5f5',
												borderRadius: '4px',
												overflow: 'auto',
												fontSize: '0.875rem',
											}}>
												{error instanceof Error ? error.message : JSON.stringify(error, null, 2)}
											</pre>
										</details>
									)}
								</>
							) : (
								<>
									<p>
										We apologize, but it seems like the page you&apos;re trying to
										connect to is experiencing technical difficulties. The link
										may be broken or the action you were attempting didn&apos;t work as
										expected.
									</p>

									<p>
										Rest assured, our team is already working to fix the problem.
										In the meantime, you can try &quot;Refresh Page&quot; or &quot;Return to
										Homepage&quot; button below.
									</p>
								</>
							)}

							<div className="d-flex">
								{showRefresh && (
									<Button
										variant="secondary"
										className="my-3"
										onClick={handleRefreshClick}
									>
										{refreshButtonText}
									</Button>
								)}
								{showReturn && (
									<Button
										variant="outline-secondary"
										className="ms-3 my-3"
										href={returnLink}
										onClick={handleReturnClick}
									>
										{returnButtonText}
									</Button>
								)}
							</div>
						</div>
						<div className="col-xxl-5 col-md-6 d-none d-md-flex align-items-center justify-content-center">
							<img
								src={imgSrc}
								alt="error"
								className="w-100"
								style={{ maxHeight: 540 }}
							/>
						</div>
					</div>
				</div>
			</main>
		</>
	);
};

export default ErrorElement;
